import { isCommissionProduct } from '@vy/services';
import type { CommissionProductVariantInput, PlanInputFormFixedProduct } from '@vy/types';
import { BILLING_INTERVALS, PRICING_MODELS } from '@vy/types';
import { sumBy, withDefault } from '@vy/utils';
import { percentageRatio } from '@vy/utils';
import { useFormContext } from 'react-hook-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import { getProductInputEstimation } from '../utils/live-preview-estimation';

export function usePlanTotalAmount() {
  const { watch } = useFormContext<PlanInputForm>();

  const billingData = watch('billingData') ?? {
    billingInterval: BILLING_INTERVALS.Monthly,
  };
  const fixedProducts = withDefault(watch('fixedProducts'), []);
  const usageProducts = withDefault(watch('usageProducts'), []);
  const products = [...fixedProducts, ...usageProducts];
  const promotions = withDefault(watch('promotionProducts'), []);

  const commissionProductsVariants = products
    .filter((product) => isCommissionProduct(product))
    .map((product) => product.variants[0]);

  const productsValueAmount = sumBy(products, (product) => getProductInputEstimation(product, billingData));
  const promotionsValueAmount = promotions.reduce(
    (acc, promotion) => acc + calculatePromotion(promotion, productsValueAmount),
    0,
  );

  return calculateCommission(
    productsValueAmount,
    promotionsValueAmount,
    commissionProductsVariants as CommissionProductVariantInput[],
  );
}

function calculatePromotion(promotion: PlanInputFormFixedProduct, productsAmount: number) {
  const { pricing } = promotion.variants[0];

  if (pricing.pricingModel === PRICING_MODELS.FixedDeduction) {
    return pricing.priceConfiguration?.amountToDeduct ?? 0;
  }

  if (pricing.pricingModel === PRICING_MODELS.PercentageDeduction) {
    const percentage = pricing.priceConfiguration?.percentageToDeduct ?? 0;

    return percentage === 0 ? 0 : productsAmount * percentageRatio(percentage);
  }

  return 0;
}

function calculateCommission(
  productsValueAmount: number,
  promotionsValueAmount: number,
  commissionProductsVariants: CommissionProductVariantInput[],
) {
  const totalAmountBeforeCommission = productsValueAmount - promotionsValueAmount;

  return commissionProductsVariants.reduce((accumulatedSum, commissionProductVariant) => {
    const commissionPercentage = withDefault(
      commissionProductVariant.pricing.priceConfiguration?.percentage,
      0,
    );
    const commission = commissionPercentage > 0 ? percentageRatio(commissionPercentage) * accumulatedSum : 0;

    return accumulatedSum + commission;
  }, totalAmountBeforeCommission);
}
